import { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router';
import { Search } from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import { Checkbox } from '@rocco/components/checkbox';
import { Input as RoccoInput } from '@rocco/components/input';
import { DisplayMode } from '@rocco/types/enum/display-mode';
import {
    FiltersProvider,
    useFiltersContext,
} from '@rocco/ui/filter-sort/components/Filters';
import { FiltersModalElement } from '@rocco/ui/filter-sort/components/filters-modal/FiltersModalElement';
import { FiltersModalLocation } from '@rocco/ui/filter-sort/components/filters-modal/elements/FiltersModalLocation';
import { FiltersModalSearchableList } from '@rocco/ui/filter-sort/components/filters-modal/elements/FiltersModalSearchableList';
import { FiltersModalPrice } from '@rocco/ui/filter-sort/components/filters-modal/elements/FiltersModalPrice';
import { FiltersModalSimpleRange } from '@rocco/ui/filter-sort/components/filters-modal/elements/FiltersModalSimpleRange';
import {
    isLocationFilterState,
    isMultipleValuesFilterState,
    isRangeFilterState,
    isSearchableListFilterState,
    isSingleValueFilterState,
} from '@rocco/ui/filter-sort/view-models/filter-state/filter-state';
import type { FilterSortOption } from '@rocco/ui/filter-sort/components/Filters';
import type {
    AggregationArgs,
    useSearchFilters,
} from '@rocco/ui/filter-sort/hooks/use-search-filters';
import type { FilterState } from '@rocco/ui/filter-sort/view-models/filter-state/filter-state';
import type { SearchableListFilterState } from '@rocco/ui/filter-sort/view-models/filter-state/searchable-list-filter-state';
import type { MultipleValuesFilterState } from '@rocco/ui/filter-sort/view-models/filter-state/multiple-values-filter-state';
import type { RangeFilterState } from '@rocco/ui/filter-sort/view-models/filter-state/range-filter-state';
import type { FilterStatesKey } from '@rocco/ui/filter-sort/view-models/filter-states';
import type { ModalFiltersConfigSection } from '@rocco/ui/filter-sort/types/filter-config-section';
import type {
    FilterConfigModalItem,
    FilterConfigModalItemSearchable,
    FilterConfigModalItemSimpleRange,
} from '@rocco/ui/filter-sort/types/filter-config-modal';
import { useUser } from '~/modules/user/hook/use-user';
import { getPermissions } from '@modules/user';
import { PermissionEnum } from 'generated/graphql';
import { useFeature } from '@modules/root';
import {
    AwardsCheckboxTree,
    groupAwardItems,
} from '@ui-rocco/components/awards-checkbox-tree/AwardsCheckboxTree';
import {
    applyAwardsSelection,
    type AwardsListItem,
} from '@modules-rocco/filters/filter-state/awards-filter-state';
import { useDirectoryBrowseTheme } from './directory-browse-theme';

export interface CategoryItem {
    id: number | string;
    title: string;
    link: string;
    /**
     * Overrides NavLink's own active detection — needed when category links
     * are query-param URLs on a single pathname (the professional tabs),
     * where every link would otherwise count as active.
     */
    isActive?: boolean;
    /**
     * Whether the category can be drilled into further. Only rows with
     * children get the forward chevron — a leaf row has nowhere to drill.
     */
    hasChildren?: boolean;
    /**
     * Renders the row as the up-one-level link: a back arrow before the
     * label instead of the forward chevron after it.
     */
    isBack?: boolean;
    /**
     * Accessible name for rows whose visible label lacks destination
     * context (the Back row). Must contain the visible label.
     */
    ariaLabel?: string;
}

interface DirectoryFilterSidebarProps {
    searchFilters: ReturnType<typeof useSearchFilters>;
    sortOptions: FilterSortOption[];
    /** Top-level categories listed above the facet sections. */
    categories?: CategoryItem[];
    /**
     * Section titles to hide. Defaults to the browse 5.0 hidden set; the
     * professional tabs pass [] to show everything their configs declare.
     */
    hiddenSectionTitles?: string[];
    /**
     * Extra classes for the sticky container — hosts whose gutter differs
     * from the browse aside (the professional tabs) override the offset here.
     */
    className?: string;
    /**
     * A group rendered above the facet sections, for a filter the shared config
     * can't express — the professional Products tab passes its brand family,
     * which scopes by a URL param of its own rather than an aggregation. Passed
     * as data so it renders with this sidebar's own section chrome and rows.
     */
    leadingGroup?: LeadingFilterGroup;
}

export interface LeadingFilterGroup {
    title: string;
    items: {
        id: string;
        label: string;
        checked: boolean;
        /** Selecting the row navigates here, so the filter stays shareable. */
        to: string;
    }[];
}

// Section titles hidden from the browse sidebar (kept in the config for the
// legacy directory / other surfaces). Pricing (Shop / On sale / Price range)
// and Downloads are back per QA — only Designer stays out of the 5.0 sidebar.
const DEFAULT_HIDDEN_SECTION_TITLES = ['Designer'];

// ---------------------------------------------------------------------------
// Chevron
// ---------------------------------------------------------------------------
const Chevron = ({ open }: { open: boolean }) => (
    <svg
        width="14"
        height="14"
        viewBox="0 0 14 14"
        fill="none"
        aria-hidden="true"
        className={cn(
            'shrink-0 transition-transform duration-200',
            open && '-scale-y-100',
        )}
    >
        <path
            d="M3 5l4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

// ---------------------------------------------------------------------------
// A single left-aligned checkbox row (Figma: 15px box, #343434, label right)
// ---------------------------------------------------------------------------
const CheckboxRow = ({
    label,
    checked,
    disabled,
    onToggle,
    isDark,
    radio = false,
    radioTabIndex,
}: {
    label: string;
    checked: boolean;
    disabled?: boolean;
    onToggle: () => void;
    isDark: boolean;
    /** Single-choice lists render the rocco radio (circle + dot). */
    radio?: boolean;
    /** Roving tabindex within the radiogroup (0 on the active radio). */
    radioTabIndex?: number;
}) => (
    <label
        className={cn(
            'flex cursor-pointer items-start gap-2',
            disabled && 'cursor-default opacity-50',
        )}
    >
        {radio ? (
            // The rocco RadioGroupItem look (bordered circle, filled centre
            // dot) without the Radix group — selection state already lives in
            // the filter state, so a plain radio-role button keeps one source
            // of truth. Buttons are labelable, so label clicks activate it.
            <button
                type="button"
                role="radio"
                aria-checked={checked}
                tabIndex={radioTabIndex}
                disabled={disabled}
                // Radios ignore re-activation of the selected option — without
                // this, Space/Enter on the focused (checked) radio would clear
                // the selection back to the resting default.
                onClick={() => !checked && onToggle()}
                className={cn(
                    // Top-aligned; nudged down to sit on the first line of a
                    // multi-line label rather than centring across all lines.
                    'mt-0.5 flex size-[0.9375rem] shrink-0 cursor-pointer items-center justify-center rounded-full border',
                    disabled && 'cursor-default',
                    isDark
                        ? cn(
                              'text-white',
                              checked ? 'border-white' : 'border-white/60',
                          )
                        : 'border-[#343434] text-[#343434]',
                )}
            >
                {checked && <span className="size-2 rounded-full bg-current" />}
            </button>
        ) : (
            <Checkbox
                checked={checked}
                disabled={disabled}
                onCheckedChange={() => onToggle()}
                className={cn(
                    'mt-0.5 size-[0.9375rem] shrink-0 rounded-[0.125rem] border',
                    isDark
                        ? 'border-white/60 data-[state=checked]:border-white'
                        : 'border-[#343434]',
                )}
            />
        )}
        <span
            className={cn(
                'text-[0.875rem] leading-[1.358]',
                isDark ? 'text-white/80' : 'text-[#343434]',
            )}
        >
            {label}
        </span>
    </label>
);

// ---------------------------------------------------------------------------
// Searchable list (Material, Brand, Suppliers, …): the Rocco searchable control
// searches its options server-side, echoing the fetched list back through
// onFilterStateUpdate. That must NOT navigate (it would refetch products and
// reset the field), so we hold the list locally and only apply live when the
// *selection* changes.
// ---------------------------------------------------------------------------
const selectionKey = (state: SearchableListFilterState) =>
    state.selectedItems
        .map((selectedItem) => selectedItem.value)
        .sort()
        .join('|');

const SidebarSearchableFilter = ({
    item,
    appliedState,
    isDark,
    onApply,
    onAggregate,
}: {
    item: FilterConfigModalItemSearchable;
    appliedState: SearchableListFilterState;
    isDark: boolean;
    onApply: (state: SearchableListFilterState) => void;
    onAggregate: (args: AggregationArgs) => void;
}) => {
    const [localState, setLocalState] = useState(appliedState);
    const appliedRef = useRef(appliedState);
    appliedRef.current = appliedState;

    // Re-sync only when the applied selection changes (after a live apply /
    // navigation) — not on every re-render, which would wipe the search list.
    const appliedSelection = selectionKey(appliedState);
    useEffect(() => {
        setLocalState(appliedRef.current);
    }, [appliedSelection]);

    return (
        <FiltersModalSearchableList
            variant="sidebar"
            isDark={isDark}
            filterConfigItem={item}
            filterState={localState}
            displayMode={DisplayMode.Desktop}
            onFilterStateUpdate={(next) => {
                setLocalState(next);
                if (selectionKey(next) !== selectionKey(localState)) {
                    onApply(next);
                }
            }}
            onAlphabeticalGrouping={onAggregate}
        />
    );
};

// ---------------------------------------------------------------------------
// Simple range (Size: Length/Width/… mm/kg). The control echoes its value on
// every keystroke (shouldAggregate=false) and applies on blur (=true), so we
// hold the value locally and only navigate on blur.
// ---------------------------------------------------------------------------
const SidebarRangeFilter = ({
    item,
    appliedState,
    isDark,
    onApply,
}: {
    item: FilterConfigModalItemSimpleRange;
    appliedState: RangeFilterState;
    isDark: boolean;
    onApply: (state: RangeFilterState) => void;
}) => {
    const [localState, setLocalState] = useState(appliedState);
    const appliedRef = useRef(appliedState);
    appliedRef.current = appliedState;
    const appliedValue = `${appliedState.value.min}-${appliedState.value.max}`;
    useEffect(() => {
        setLocalState(appliedRef.current);
    }, [appliedValue]);

    return (
        <FiltersModalSimpleRange
            variant="sidebar"
            isDark={isDark}
            filterConfigItem={item}
            filterState={localState}
            displayMode={DisplayMode.Desktop}
            onFilterStateUpdate={(next, shouldAggregate) => {
                setLocalState(next);
                if (shouldAggregate) onApply(next);
            }}
        />
    );
};

// ---------------------------------------------------------------------------
// Multi-select list (Business Focus, Professional Types, …) — a checkbox per
// item, with a client-side search box once the list is long enough to be worth
// filtering (e.g. the 30+ Professional Types). Search is purely local: it never
// navigates, it just narrows the visible checkboxes.
// ---------------------------------------------------------------------------
const MULTI_VALUE_SEARCH_THRESHOLD = 10;

// ---------------------------------------------------------------------------
// Awards — the flat awards list grouped under institutions as an accessible
// checkbox tree (parent checkbox + separate expand button per institution).
// ---------------------------------------------------------------------------
const SidebarAwardsTreeFilter = ({
    state,
    isDark,
    onApply,
}: {
    state: SearchableListFilterState;
    isDark: boolean;
    onApply: (next: SearchableListFilterState) => void;
}) => {
    const groups = useMemo(
        () => groupAwardItems(state.items as AwardsListItem[]),
        [state.items],
    );
    const checkedValues = useMemo(
        () =>
            new Set(
                state.selectedItems.map((selectedItem) =>
                    String(selectedItem.value),
                ),
            ),
        [state.selectedItems],
    );

    const handleToggle = (values: string[], checked: boolean) => {
        const nextValues = applyAwardsSelection(
            checkedValues,
            state.items.map((item) => String(item.value)),
            values,
            checked,
        );
        const items = state.items.map((item) => ({
            ...item,
            checked: nextValues.has(String(item.value)),
        }));
        onApply({
            ...state,
            items,
            selectedItems: items.filter((item) => item.checked),
        });
    };

    return (
        <AwardsCheckboxTree
            groups={groups}
            checkedValues={checkedValues}
            onToggle={handleToggle}
            isDark={isDark}
        />
    );
};

const SidebarMultiValueFilter = ({
    state,
    isDark,
    onApply,
    groupLabel,
}: {
    state: MultipleValuesFilterState;
    isDark: boolean;
    onApply: (next: MultipleValuesFilterState) => void;
    /** Accessible name for the radiogroup on single-choice lists. */
    groupLabel?: string;
}) => {
    const [query, setQuery] = useState('');
    const showSearch = state.items.length > MULTI_VALUE_SEARCH_THRESHOLD;
    const trimmed = query.trim().toLowerCase();
    const visibleItems = useMemo(
        () =>
            trimmed
                ? state.items.filter(
                      (option) =>
                          // Keep checked options visible even when they don't
                          // match, so an active selection is never hidden (and
                          // can still be unchecked) while a search is typed.
                          option.checked ||
                          (option.label ?? String(option.value))
                              .toLowerCase()
                              .includes(trimmed),
                  )
                : state.items,
        [state.items, trimmed],
    );

    const toggle = (
        optionValue: MultipleValuesFilterState['items'][number]['value'],
    ) => {
        const items = state.items.map((option) => {
            if (option.value === optionValue) {
                return { ...option, checked: !option.checked };
            }
            // Single-choice lists (Project owner, Projects listed) behave
            // like radios: picking an option clears the others.
            return state.allowMultipleSelectedItems
                ? option
                : { ...option, checked: false };
        });
        onApply({
            ...state,
            items,
            selectedItems: items.filter((option) => option.checked),
        });
    };

    const isRadioGroup = !state.allowMultipleSelectedItems;
    // Single-choice lists with a numeric zero option (Projects listed's
    // "Any") treat it as the resting default — it applies no URL param, so it
    // can never be "selected" in state.
    const displaysChecked = (
        option: MultipleValuesFilterState['items'][number],
    ) =>
        option.checked ||
        (isRadioGroup &&
            String(option.value) === '0' &&
            state.selectedItems.length === 0);
    // Roving tabindex: only the checked radio (or the first, when nothing
    // displays checked) is a tab stop; arrows move selection within the group.
    const tabbableIndex = Math.max(
        0,
        visibleItems.findIndex((option) => displaysChecked(option)),
    );
    const handleRadioKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
        const forward = event.key === 'ArrowDown' || event.key === 'ArrowRight';
        if (!forward && event.key !== 'ArrowUp' && event.key !== 'ArrowLeft') {
            return;
        }
        const radios = [
            ...event.currentTarget.querySelectorAll<HTMLButtonElement>(
                '[role="radio"]',
            ),
        ];
        const current = radios.indexOf(event.target as HTMLButtonElement);
        if (current === -1) return;
        event.preventDefault();
        const next =
            radios[
                (current + (forward ? 1 : -1) + radios.length) % radios.length
            ];
        // Per the ARIA radio pattern, moving focus selects the radio.
        next?.focus();
        next?.click();
    };

    return (
        <div className="flex flex-col gap-1.5">
            {showSearch && (
                <div className="mb-1">
                    {/* Same filled-grey field as the searchable lists (Brand,
                        Suppliers) so every in-section search reads the same. */}
                    <RoccoInput
                        color="gray"
                        text="small"
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder="Search"
                        aria-label="Filter options"
                        showClearButton={true}
                        clearButtonClassName={isDark ? 'text-white' : undefined}
                        postfix={
                            <Search
                                className={cn(
                                    'size-4.5',
                                    isDark && 'text-white/70',
                                )}
                            />
                        }
                        className={cn(
                            'h-[2.625rem] rounded-[0.25rem] px-[0.5625rem] text-[1rem] placeholder:text-[#717171]',
                            isDark
                                ? 'bg-white/10 text-white placeholder:text-white/40'
                                : 'bg-[#f4f4f4]',
                        )}
                    />
                </div>
            )}
            <div
                className="flex flex-col gap-1.5"
                role={isRadioGroup ? 'radiogroup' : undefined}
                aria-label={isRadioGroup ? groupLabel : undefined}
                onKeyDown={isRadioGroup ? handleRadioKeyDown : undefined}
            >
                {visibleItems.map((option, index) => (
                    <CheckboxRow
                        key={String(option.value)}
                        label={option.label ?? String(option.value)}
                        checked={displaysChecked(option)}
                        isDark={isDark}
                        radio={isRadioGroup}
                        radioTabIndex={index === tabbableIndex ? 0 : -1}
                        onToggle={() => toggle(option.value)}
                    />
                ))}
            </div>
            {showSearch && visibleItems.length === 0 && (
                <span
                    className={cn(
                        'py-1 text-[0.875rem]',
                        isDark ? 'text-white/50' : 'text-[#9a9a9a]',
                    )}
                >
                    No matching options
                </span>
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Section body: custom checkbox rows for boolean / multi-value filters, and
// the Rocco control for the heavier types (range / location / searchable).
// ---------------------------------------------------------------------------
const SectionBody = ({
    section,
    isDark,
}: {
    section: ModalFiltersConfigSection;
    isDark: boolean;
}) => {
    const { searchFilters } = useFiltersContext();
    const awardBadgesEnabled = useFeature('award_badges');
    const {
        filterStates,
        updateFilter,
        getUpdatedFilterStates,
        handleAggregate,
    } = searchFilters;

    const applyState = (
        filterKey: FilterStatesKey,
        newState: FilterState,
        isFacet = false,
    ) => {
        updateFilter(
            getUpdatedFilterStates(filterStates, filterKey, newState, isFacet),
        );
    };

    const renderItem = (item: FilterConfigModalItem) => {
        const state = filterStates.states[item.filterKey] ?? null;

        // Awards — institution-grouped checkbox tree, feature-gated. Checked
        // before the searchable branch: the state is a searchable list but
        // renders as a tree.
        if (item.filterKey === 'awards') {
            if (
                !awardBadgesEnabled ||
                !state ||
                !isSearchableListFilterState(state) ||
                state.items.length === 0
            ) {
                return null;
            }
            return (
                <SidebarAwardsTreeFilter
                    key={item.filterKey}
                    state={state}
                    isDark={isDark}
                    onApply={(next) => applyState(item.filterKey, next)}
                />
            );
        }

        // Location — filled input + plain "Use my current location" (Figma).
        if (state && isLocationFilterState(state) && item.type === 'default') {
            return (
                <FiltersModalLocation
                    key={item.filterKey}
                    filterConfigItem={item}
                    filterState={state}
                    displayMode={DisplayMode.Desktop}
                    variant="sidebar"
                    isDark={isDark}
                    inputClassName={
                        isDark
                            ? 'bg-white/10 text-white placeholder:text-white/40'
                            : undefined
                    }
                    currentLocationClassName={
                        isDark ? 'text-white/80' : undefined
                    }
                    onFilterStateUpdate={(newState) => {
                        // The control echoes its state on mount (and on every
                        // keystroke via re-render); navigating then would remount
                        // it and swallow typing. Only apply when the *selected*
                        // location actually changes.
                        const nextLoc = isLocationFilterState(newState)
                            ? newState.selectedLocation
                            : null;
                        const currentLoc = isLocationFilterState(state)
                            ? state.selectedLocation
                            : null;
                        if (
                            JSON.stringify(nextLoc) ===
                            JSON.stringify(currentLoc)
                        ) {
                            return;
                        }
                        applyState(item.filterKey, newState);
                    }}
                />
            );
        }

        // Searchable list (Material, Brand, Suppliers, …) — search input + list.
        // Checked before the multi-value branch because searchable states also
        // satisfy isMultipleValuesFilterState.
        if (
            state &&
            item.type === 'searchable' &&
            isSearchableListFilterState(state)
        ) {
            return (
                <SidebarSearchableFilter
                    key={item.filterKey}
                    item={item}
                    appliedState={state}
                    isDark={isDark}
                    onApply={(next) => applyState(item.filterKey, next)}
                    onAggregate={(args) =>
                        handleAggregate({ ...args, filterStates })
                    }
                />
            );
        }

        // Simple range (Size) — filled grey min/max inputs, apply on blur.
        if (state && item.type === 'simpleRange' && isRangeFilterState(state)) {
            return (
                <SidebarRangeFilter
                    key={item.filterKey}
                    item={item}
                    appliedState={state}
                    isDark={isDark}
                    onApply={(next) => applyState(item.filterKey, next)}
                />
            );
        }

        // Boolean toggle (Shop, In Stock, …) — left-aligned checkbox.
        if (state && isSingleValueFilterState(state)) {
            return (
                <CheckboxRow
                    key={item.filterKey}
                    label={item.label ?? ''}
                    checked={state.checked}
                    disabled={state.disabled}
                    isDark={isDark}
                    onToggle={() =>
                        applyState(item.filterKey, {
                            ...state,
                            checked: !state.checked,
                        })
                    }
                />
            );
        }

        // Multi-select list (Business Focus, …) — a checkbox per item. Excludes
        // searchable (handled above) and colour (Rocco swatch control below).
        if (
            state &&
            isMultipleValuesFilterState(state) &&
            item.type !== 'color' &&
            item.type !== 'searchable'
        ) {
            return (
                <SidebarMultiValueFilter
                    key={item.filterKey}
                    state={state}
                    isDark={isDark}
                    groupLabel={item.label ?? section.title}
                    onApply={(next) => applyState(item.filterKey, next)}
                />
            );
        }

        // Everything else (colour swatches, ranges, location, searchable list)
        // reuses the Rocco control off the same live search-filters instance.
        const facetState =
            state ??
            filterStates.facet?.facetOptions?.find(
                (facet) => facet.id === item.filterKey,
            )?.state ??
            null;
        if (!facetState) return null;
        const isFacet = !filterStates.states[item.filterKey];
        // Pricing range — the shared slider control, but with the sidebar's
        // filled grey min/max inputs instead of the modal's outlined ones.
        if (item.type === 'price' && isRangeFilterState(facetState)) {
            return (
                <FiltersModalPrice
                    key={item.filterKey}
                    variant="sidebar"
                    isDark={isDark}
                    filterConfigItem={item}
                    filterState={facetState}
                    displayMode={DisplayMode.Desktop}
                    onFilterStateUpdate={(next) =>
                        applyState(item.filterKey, next, isFacet)
                    }
                />
            );
        }
        return (
            <FiltersModalElement
                key={item.filterKey}
                displayMode={DisplayMode.Desktop}
                filterConfigItem={item}
                filterState={facetState}
                onUpdateFilterState={(filterKey, newState) =>
                    applyState(filterKey, newState, isFacet)
                }
                onAlphabeticalGrouping={(args) =>
                    handleAggregate({ ...args, filterStates })
                }
            />
        );
    };

    return (
        <div
            className={cn(
                'flex flex-col gap-1.5 pt-3.5 pb-5',
                // Reuse Rocco controls read on dark: force their labels white.
                isDark && '[&_label]:text-white/80 [&_span]:text-white/80',
            )}
        >
            {section.filters.map(renderItem)}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Collapsible section (Figma: 1px black top border, 18px capitalize title,
// chevron toggle, reduced heading spacing).
// ---------------------------------------------------------------------------
const FilterSection = ({
    section,
    isDark,
    defaultOpen,
}: {
    section: ModalFiltersConfigSection;
    isDark: boolean;
    defaultOpen: boolean;
}) => {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div
            className={cn(
                'border-t',
                isDark ? 'border-white/25' : 'border-[#1c1c1c]',
            )}
        >
            <button
                type="button"
                onClick={() => setOpen((prev) => !prev)}
                aria-expanded={open}
                className="flex w-full items-center justify-between py-[0.5625rem] text-left"
            >
                <span
                    className={cn(
                        'text-[1.125rem] leading-[1.04] font-normal capitalize',
                        isDark ? 'text-white' : 'text-black',
                    )}
                >
                    {section.title}
                </span>
                <span className={isDark ? 'text-white' : 'text-black'}>
                    <Chevron open={open} />
                </span>
            </button>
            {open && <SectionBody section={section} isDark={isDark} />}
        </div>
    );
};

/** {@link LeadingFilterGroup} rendered with the sidebar's own section chrome. */
const LeadingSection = ({
    group,
    isDark,
}: {
    group: LeadingFilterGroup;
    isDark: boolean;
}) => {
    const [open, setOpen] = useState(true);
    const navigate = useNavigate();
    return (
        <div
            className={cn(
                'border-t',
                isDark ? 'border-white/25' : 'border-[#1c1c1c]',
            )}
        >
            <button
                type="button"
                onClick={() => setOpen((prev) => !prev)}
                aria-expanded={open}
                className="flex w-full items-center justify-between py-[0.5625rem] text-left"
            >
                <span
                    className={cn(
                        'text-[1.125rem] leading-[1.04] font-normal capitalize',
                        isDark ? 'text-white' : 'text-black',
                    )}
                >
                    {group.title}
                </span>
                <span className={isDark ? 'text-white' : 'text-black'}>
                    <Chevron open={open} />
                </span>
            </button>
            {open ? (
                <div className="flex flex-col gap-2 pt-1 pb-4">
                    {group.items.map((item) => (
                        <CheckboxRow
                            key={item.id}
                            label={item.label}
                            checked={item.checked}
                            isDark={isDark}
                            onToggle={() =>
                                navigate(item.to, {
                                    preventScrollReset: true,
                                })
                            }
                        />
                    ))}
                </div>
            ) : null}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Sidebar
// ---------------------------------------------------------------------------
const DirectoryFilterSidebarBody = ({
    categories,
    hiddenSectionTitles,
    leadingGroup,
    className,
}: {
    categories?: CategoryItem[];
    hiddenSectionTitles?: string[];
    className?: string;
    leadingGroup?: LeadingFilterGroup;
}) => {
    const { searchFilters } = useFiltersContext();
    const { config, filterStates } = searchFilters;
    const isDark = useDirectoryBrowseTheme() === 'dark';
    const awardBadgesEnabled = useFeature('award_badges');

    const hasMarketingView = getPermissions(
        useUser(),
        PermissionEnum.BmMarketingView,
    );

    const hiddenTitles = useMemo(
        () => new Set(hiddenSectionTitles ?? DEFAULT_HIDDEN_SECTION_TITLES),
        [hiddenSectionTitles],
    );

    // A section only renders when at least one of its filters has something
    // usable to show: options for lists, a real span for ranges, an enabled
    // (or applied) checkbox for booleans. This hides sections whose data is
    // empty for the current scope — e.g. a professional with no materials or
    // designers, an Availability group whose counts are all zero, the Project
    // owner section for non-manufacturers, and Awards when the feature is off.
    const sectionHasContent = useMemo(
        () => (section: ModalFiltersConfigSection) =>
            section.filters.some((item) => {
                if (item.filterKey === 'awards') {
                    const awardsState = filterStates.states.awards;
                    return (
                        awardBadgesEnabled &&
                        !!awardsState &&
                        isSearchableListFilterState(awardsState) &&
                        awardsState.items.length > 0
                    );
                }

                const state =
                    filterStates.states[item.filterKey] ??
                    filterStates.facet?.facetOptions?.find(
                        (facet) => facet.id === item.filterKey,
                    )?.state ??
                    null;
                if (!state) return false;

                if (isSingleValueFilterState(state)) {
                    return !state.disabled || state.checked;
                }
                // Searchable lists satisfy the multi-value guard too.
                if (isMultipleValuesFilterState(state)) {
                    return (
                        state.items.length > 0 || state.selectedItems.length > 0
                    );
                }
                if (isRangeFilterState(state)) {
                    return state.range.max > state.range.min;
                }
                return true;
            }),
        [filterStates, awardBadgesEnabled],
    );

    const sections = useMemo(
        () =>
            config.modalFilters.filter(
                (section) =>
                    (!section.adminOnly || hasMarketingView) &&
                    !hiddenTitles.has(section.title) &&
                    sectionHasContent(section),
            ),
        [
            config.modalFilters,
            hasMarketingView,
            hiddenTitles,
            sectionHasContent,
        ],
    );

    return (
        // Align the panel with the page gutter (the grid carries its own px-12);
        // the inner column is capped near the Figma 262px so it doesn't sprawl the
        // 460px aside — the extra width is the gap before the grid. Sticks in view
        // as the (very long) infinite-scroll grid scrolls past; scrolls internally
        // when the expanded filters exceed the viewport.
        //
        // The internal scroll gets a visible affordance on hover: the thin
        // track is always reserved (no layout shift) and the thumb fades in
        // while the pointer is over the panel, so cut-off filter sections
        // read as scrollable instead of missing.
        <div
            className={cn(
                // pr-2 keeps a slight gap between the content edge and the
                // scrollbar track when the panel overflows.
                'sticky top-6 flex max-h-[calc(100vh-3rem)] w-[20rem] flex-col overflow-x-visible overflow-y-auto pr-2 pl-4 md:ml-12 md:pl-0',
                '[scrollbar-width:thin] [scrollbar-color:transparent_transparent]',
                '[&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-transparent [&::-webkit-scrollbar-track]:bg-transparent',
                isDark
                    ? 'hover:[scrollbar-color:rgba(255,255,255,0.35)_transparent] hover:[&::-webkit-scrollbar-thumb]:bg-white/35'
                    : 'hover:[scrollbar-color:rgba(0,0,0,0.3)_transparent] hover:[&::-webkit-scrollbar-thumb]:bg-black/30',
                className,
            )}
        >
            {/* Category list */}
            {categories && categories.length > 0 && (
                // Tight leading keeps a wrapped title (Central Heating &
                // Cooling) reading as one item; the nav gap carries the
                // between-item rhythm the old leading-[1.5] line boxes gave.
                <nav
                    aria-label="Categories"
                    className="flex flex-col gap-y-[0.625rem] pb-10"
                >
                    {categories.map((category) => (
                        <NavLink
                            key={category.id}
                            to={category.link}
                            preventScrollReset
                            aria-label={category.ariaLabel}
                            className={({ isActive }) =>
                                cn(
                                    'group flex items-start gap-1',
                                    // The Back row sits a step smaller than the
                                    // category rows so navigation reads apart
                                    // from the list it heads. Size and leading
                                    // ride one compound class: tw-merge treats
                                    // a separate font-size class as conflicting
                                    // with an earlier leading-* and drops it.
                                    category.isBack
                                        ? 'text-[1rem]/[1.2]'
                                        : 'text-[1.25rem]/[1.2]',
                                    isDark ? 'text-white/70' : 'text-[#343434]',
                                    (category.isActive ?? isActive) &&
                                        (isDark
                                            ? 'text-white'
                                            : 'font-medium text-black'),
                                )
                            }
                        >
                            {/* Back rows point the other way: a left arrow
                                before the label, always visible — it's the
                                row's whole affordance, not a drill hint. */}
                            {/* h-[1lh] + items-start on both arrows: the icon
                                box spans exactly the first line box, and the
                                square viewBox letterboxes (xMidYMid) the glyph
                                to its vertical centre — centred beside a
                                one-line title, pinned to the top line when it
                                wraps, at every viewport rem scale. */}
                            {category.isBack && (
                                <svg
                                    width="16"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    aria-hidden="true"
                                    className="h-[1lh] shrink-0"
                                >
                                    <path
                                        d="M12.5 5l-5 5 5 5"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            )}
                            <span>{category.title}</span>
                            {/* Right-chevron marks rows that drill into child
                                categories, revealing on hover (Figma 2354:59607).
                                Hover has no touch equivalent, so it stays always
                                visible below md; the hover-reveal is desktop-only
                                (md and above). Leaf rows get no chevron. */}
                            {!category.isBack && category.hasChildren && (
                                <svg
                                    width="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    aria-hidden="true"
                                    className="h-[1lh] shrink-0 opacity-100 transition-opacity duration-150 md:opacity-0 md:group-hover:opacity-100"
                                >
                                    <path
                                        d="M7.5 5l5 5-5 5"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            )}
                        </NavLink>
                    ))}
                </nav>
            )}

            {leadingGroup ? (
                <LeadingSection group={leadingGroup} isDark={isDark} />
            ) : null}

            {/* Facet sections — first one open, rest collapsed. */}
            {sections.map((section, index) => (
                <FilterSection
                    key={section.title}
                    section={section}
                    isDark={isDark}
                    defaultOpen={index === 0}
                />
            ))}
        </div>
    );
};

export const DirectoryFilterSidebar = ({
    searchFilters,
    sortOptions,
    categories,
    hiddenSectionTitles,
    className,
    leadingGroup,
}: DirectoryFilterSidebarProps) => {
    return (
        <FiltersProvider
            searchFilters={searchFilters}
            sortOptions={sortOptions}
            displayMode={DisplayMode.Desktop}
            hideFacetFilters={false}
        >
            <DirectoryFilterSidebarBody
                categories={categories}
                hiddenSectionTitles={hiddenSectionTitles}
                className={className}
                leadingGroup={leadingGroup}
            />
        </FiltersProvider>
    );
};
